import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMyDashboard } from '../hooks/useMyDashboard'
import { useUpdateNotificationPreference } from '../hooks/useNotifications'
import { useAuthStore } from '../store/auth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EventBadge } from '../components/ui/EventBadge'
import { getEventType } from '../components/ui/eventTypes'
import { useIsMobile } from '../hooks/useIsMobile'
import { Calendar, Clock, Users, ChevronRight, ArrowRight } from 'lucide-react'
import type { UserRsvpEntry, Church, HostedEventSummary } from '@fellowship/shared'

const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function RsvpCard({ entry, past }: { entry: UserRsvpEntry; past?: boolean }) {
  const { occurrence, rsvp, church } = entry
  const title = occurrence.overrideTitle ?? occurrence.event.title

  return (
    <Link to={`/events/${occurrence.event.id}`} style={{ textDecoration: 'none' }}>
      <article
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
          padding: '14px 16px',
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 16,
          cursor: 'pointer',
          opacity: past ? 0.65 : 1,
          transition: 'background 0.12s',
        }}
      >
        <div style={{ flexShrink: 0, marginTop: 2 }}>
          <EventBadge type={occurrence.event.eventType} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--ink)',
              lineHeight: 1.25,
              marginBottom: 4,
            }}
          >
            {title}
          </div>
          {church && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
              {church.name}
            </div>
          )}
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--ink-2)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={11} /> {formatDate(occurrence.startsAt)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={11} /> {formatTime(occurrence.startsAt)}
            </span>
          </div>
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 700,
            padding: '3px 10px',
            borderRadius: 999,
            background:
              rsvp.attendanceStatus === 'going' ? 'var(--primary-soft)' : 'var(--surface-2)',
            color: rsvp.attendanceStatus === 'going' ? 'var(--primary)' : 'var(--muted)',
          }}
        >
          {rsvp.attendanceStatus === 'going' ? 'Going' : 'Maybe'}
        </span>
      </article>
    </Link>
  )
}

function HostedEventCard({ summary }: { summary: HostedEventSummary }) {
  const t = getEventType(summary.eventType)
  const hasUpcoming = summary.nextOccurrenceId !== null

  return (
    <article
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        padding: '16px',
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderRadius: 16,
        borderLeft: `3px solid ${t.color}`,
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <EventBadge type={summary.eventType} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link to={`/events/${summary.id}`} style={{ textDecoration: 'none' }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--ink)',
              lineHeight: 1.25,
              marginBottom: 6,
            }}
          >
            {summary.title}
          </div>
        </Link>
        <div
          style={{
            display: 'flex',
            gap: 12,
            fontSize: 12,
            color: 'var(--ink-2)',
            flexWrap: 'wrap',
          }}
        >
          {hasUpcoming ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={11} /> {formatDate(summary.nextOccurrenceStartsAt!)}
            </span>
          ) : (
            <span style={{ color: 'var(--muted)' }}>No upcoming dates</span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Users size={11} /> {summary.rsvpCount} going
          </span>
          {summary.pendingCount > 0 && (
            <span style={{ fontWeight: 600, color: '#d97706' }}>
              {summary.pendingCount} pending
            </span>
          )}
        </div>
      </div>
      {hasUpcoming ? (
        <Link
          to={`/events/${summary.id}/occurrences/${summary.nextOccurrenceId}/manage`}
          style={{ textDecoration: 'none', flexShrink: 0 }}
          aria-label={`Manage guests for ${summary.title}`}
        >
          <span
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontWeight: 600,
              color: '#fff',
              background: GRADIENT,
              boxShadow: '0 3px 8px rgba(59,76,202,.25)',
            }}
          >
            Manage <ArrowRight size={11} />
          </span>
        </Link>
      ) : (
        <Link to={`/events/${summary.id}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
          <span
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
              background: 'transparent',
            }}
          >
            View <ArrowRight size={11} />
          </span>
        </Link>
      )}
    </article>
  )
}

function ChurchCard({ church }: { church: Church }) {
  return (
    <Link to={`/churches/${church.slug}`} style={{ textDecoration: 'none' }}>
      <article
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          padding: '12px 14px',
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 14,
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            flexShrink: 0,
            background: 'var(--primary-soft)',
            border: '1px solid var(--line-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {church.logoUrl ? (
            <img
              src={church.logoUrl}
              alt={church.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{ width: 18, height: 18, borderRadius: 4, background: GRADIENT, opacity: 0.6 }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>
            {church.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {church.city}, {church.state} · {church.followerCount} followers
          </div>
        </div>
        <ChevronRight size={14} color="var(--muted)" style={{ flexShrink: 0 }} />
      </article>
    </Link>
  )
}

type Tab = 'attending' | 'hosting'

export default function MyEventsPage() {
  const { user } = useAuthStore()
  const isMobile = useIsMobile()
  const { data, isLoading } = useMyDashboard()
  const updatePref = useUpdateNotificationPreference()
  const [tab, setTab] = useState<Tab>('attending')

  const dashboard = data?.data

  return (
    <PublicLayout activeNav="Discover">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '24px 16px 60px' : '40px 40px 80px' }}>
        {/* Page header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 28,
          }}
        >
          <div>
            <h1
              style={{
                margin: '0 0 6px',
                fontSize: 40,
                fontWeight: 600,
                letterSpacing: '-0.025em',
                color: 'var(--ink)',
              }}
            >
              My events
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--muted)' }}>
              Welcome back, {user?.displayName ?? 'friend'}.
            </p>
          </div>
        </div>

        {/* Tab row */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {(['attending', 'hosting'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                height: 36,
                padding: '0 16px',
                borderRadius: 999,
                border: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13.5,
                fontWeight: 600,
                textTransform: 'capitalize',
                background: tab === t ? GRADIENT : 'var(--surface-2)',
                color: tab === t ? '#fff' : 'var(--ink-2)',
                boxShadow: tab === t ? '0 4px 12px rgba(59,76,202,.25)' : 'none',
              }}
            >
              {t}
              {t === 'hosting' && (dashboard?.hostedEvents.length ?? 0) > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    fontSize: 10,
                    fontWeight: 700,
                    background: tab === t ? 'rgba(255,255,255,0.25)' : 'var(--line-soft)',
                    color: tab === t ? '#fff' : 'var(--muted)',
                  }}
                >
                  {dashboard!.hostedEvents.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {isLoading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</p>}

        {!isLoading && dashboard && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) 300px',
              gap: isMobile ? 24 : 32,
              alignItems: 'start',
            }}
          >
            {/* Main content */}
            <div>
              {tab === 'attending' && (
                <>
                  <section aria-label="Upcoming events">
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--muted)',
                        marginBottom: 12,
                      }}
                    >
                      Upcoming · {dashboard.upcoming.length}
                    </div>
                    {dashboard.upcoming.length === 0 ? (
                      <div
                        style={{
                          padding: 28,
                          background: 'var(--surface)',
                          border: '1px solid var(--line-soft)',
                          borderRadius: 16,
                          textAlign: 'center',
                        }}
                      >
                        <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--muted)' }}>
                          No upcoming RSVPs.
                        </p>
                        <Link
                          to="/search"
                          style={{
                            display: 'inline-block',
                            fontSize: 13,
                            color: 'var(--primary)',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          Find events nearby →
                        </Link>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {dashboard.upcoming.map((entry) => (
                          <RsvpCard key={entry.rsvp.id} entry={entry} />
                        ))}
                      </div>
                    )}
                  </section>

                  {dashboard.past.length > 0 && (
                    <section aria-label="Past events" style={{ marginTop: 36 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'var(--muted)',
                          marginBottom: 12,
                        }}
                      >
                        Past events
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {dashboard.past.map((entry) => (
                          <RsvpCard key={entry.rsvp.id} entry={entry} past />
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}

              {tab === 'hosting' && (() => {
                const upcomingHosted = dashboard.hostedEvents.filter((e) => e.nextOccurrenceStartsAt !== null)
                const pastHosted = dashboard.hostedEvents.filter((e) => e.nextOccurrenceStartsAt === null)
                return (
                  <section aria-label="Events you're hosting">
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--muted)',
                        marginBottom: 12,
                      }}
                    >
                      Hosting · {upcomingHosted.length}
                    </div>
                    {dashboard.hostedEvents.length === 0 ? (
                      <div
                        style={{
                          padding: 32,
                          background: 'var(--surface)',
                          border: '1px solid var(--line-soft)',
                          borderRadius: 16,
                          textAlign: 'center',
                        }}
                      >
                        <p
                          style={{
                            margin: '0 0 6px',
                            fontSize: 15,
                            fontWeight: 600,
                            color: 'var(--ink)',
                          }}
                        >
                          No hosted events yet.
                        </p>
                        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted)' }}>
                          Host your own potluck, hike, study, or social.
                        </p>
                        <Link
                          to="/events/new"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 13,
                            color: 'var(--primary)',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          Host your first event →
                        </Link>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {upcomingHosted.map((summary) => (
                            <HostedEventCard key={summary.id} summary={summary} />
                          ))}
                        </div>
                        {upcomingHosted.length === 0 && (
                          <div style={{ padding: '16px 0', fontSize: 13, color: 'var(--muted)' }}>
                            No upcoming hosted events.
                          </div>
                        )}
                        {pastHosted.length > 0 && (
                          <section aria-label="Past hosted events" style={{ marginTop: 36 }}>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: 'var(--muted)',
                                marginBottom: 12,
                              }}
                            >
                              Past hosted events
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.65 }}>
                              {pastHosted.map((summary) => (
                                <HostedEventCard key={summary.id} summary={summary} />
                              ))}
                            </div>
                          </section>
                        )}
                      </>
                    )}
                  </section>
                )
              })()}
            </div>

            {/* Sidebar */}
            <aside>
              <section aria-label="Followed churches">
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    marginBottom: 12,
                  }}
                >
                  Following · {dashboard.followedChurches.length}
                </div>
                {dashboard.followedChurches.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      background: 'var(--surface)',
                      border: '1px solid var(--line-soft)',
                      borderRadius: 14,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>
                      Follow a church to see their events here.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {dashboard.followedChurches.map((church) => (
                      <ChurchCard key={church.id} church={church} />
                    ))}
                  </div>
                )}
              </section>

              {/* Notification pref */}
              <div
                style={{
                  marginTop: 16,
                  padding: 14,
                  background: 'var(--surface)',
                  borderRadius: 14,
                  border: '1px solid var(--line-soft)',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    gap: 10,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                      New event emails
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                      Notify me when followed churches post events
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    aria-label="Email me when followed churches post new events"
                    checked={user?.notifyNewEvents ?? true}
                    onChange={(e) => updatePref.mutate(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                  />
                </label>
              </div>
            </aside>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
